import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMantineColorScheme } from '@mantine/core';
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput.js';
import MyUserContext from '../myUserContext.js';
import ArtEventDropdown from './ArtEventDropdown'; // Import ArtEventDropdown component

// Pill background: opaque Mantine default in BOTH schemes. The
// previous light-mode value used a transparent overlay, so the pill
// composited differently against a plain page body vs a tinted
// Paper — the same component looked different on /arts/{id} vs
// /users/:id/likes. Opaque = same color everywhere.
const pillBg = () => 'var(--mantine-color-default)';

const CardEventArtChatMini = ({ art, onFirstTagAdded, initialEvent, readOnly, libraryLinkText, fullWidth = false }) => {
  const myUser = useContext(MyUserContext);
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  // For my event. When the caller passes `initialEvent`, seed state
  // from it and skip the network fetch — callers that already have the
  // full event on hand (e.g. /users/:id/likes quick-look) can avoid an
  // extra GET on every art-thumb click.
  const [artEvent, setArtEvent] = useState(null);
  const [myEvent, setMyEvent] = useState(initialEvent || null);
  useEffect(() => {
    if (initialEvent) {
      // Caller already handed us the event — no refetch needed.
      return;
    }
    if (art.myEventId) {
      axiosInstance
        .get(`/artEvents/${art.myEventId}`)
        .then((response) => {
          setArtEvent(response.data);
          setMyEvent(response.data.event);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [art.myEventId, initialEvent]);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  const [isArtEventDropdownOpen, setIsArtEventDropdownOpen] = useState(false); // State for dropdown visibility

  // Bg-tint the pill when the user hovers the interactive bits
  // (library link OR tags row), not when the cursor is over the
  // empty padding. No cursor-pointer on the pill itself — hover
  // is a visual affordance, not a click target.
  const pillRef = useRef(null);
  const setPillHover = (on) => {
    const el = pillRef.current;
    if (!el) return;
    el.style.background = on
      ? 'var(--mantine-color-default-hover)'
      : pillBg(colorScheme);
  };

  return (
    <div className={fullWidth ? "w-full" : "mx-auto max-w-xl"}>

      {/* Row: [prompt?] [tags] [chat icon] — visually a single pill like the
          paperplane / calendar rows on ArtPage. Tags stay inline-editable
          in both the "no library entry yet" and "already in library" states;
          only the chat icon opens the discussion overlay. The pill itself
          is no longer clickable and no longer highlights on hover — only
          the chat-icon button does. Prompt shows inside the pill when the
          art isn't yet in the user's library. */}
      <div className="flex justify-center">
      <div
        ref={pillRef}
        className={myEvent
          ? "flex flex-col items-center gap-2 px-3 pt-2 pb-3 rounded-2xl transition-colors"
          : "flex flex-col items-center gap-1"}
        style={myEvent
          ? { background: pillBg(colorScheme), minHeight: '48px', width: 'max-content', maxWidth: '100%' }
          : undefined}
        onMouseEnter={myEvent ? () => setPillHover(true) : undefined}
        onMouseLeave={myEvent ? () => setPillHover(false) : undefined}
      >
        {!art.myEventId && !myEvent && (() => {
          const noun = art.category === 1
            ? (language === "ru" ? "фильм" : "movie")
            : art.category === 2
              ? (language === "ru" ? "игру" : "game")
              : art.category === 3
                ? (language === "ru" ? "книгу" : "book")
                : (language === "ru" ? "объект" : "item");
          const text = language === "ru"
            ? `Выберите тэг, чтобы добавить ${noun} в свою библиотеку`
            : `Select tag to add the ${noun} to your library`;
          return (
            <span className="text-sm text-center" style={{ color: 'var(--mantine-color-dimmed)' }}>{text}</span>
          );
        })()}
        {myEvent && (
          <Link
            to={`/artEvents/${myEvent.id}`}
            className="text-sm underline-offset-2 hover:underline whitespace-nowrap px-4"
            style={{ color: 'var(--mantine-color-blue-text)' }}
          >
            {libraryLinkText || (language === 'ru' ? 'Перейти в мою библиотеку' : 'Go to my library')}
          </Link>
        )}
        <div className="flex items-center gap-2">
          <TagsInput
            key={myEvent?.id ?? 'new'}
            language={language}
            editable={!readOnly}
            flush
            artCategory={art.category}
            tags={myEvent?.eventTags || []}
            onSetGlobalManualTag={(tag) => {
              if (myEvent === null) {
                // First tag ever — event doesn't exist yet. POST /artEvents
                // still takes the name (not id) for now.
                return axiosInstance
                  .post(`/artEvents`, { globalManualTag: tag.name, artId: art.id, type: "like" /*todo: remove once backend is ready, type is legacy*/ })
                  .then((response) => {
                    setMyEvent(response.data.event);
                    if (onFirstTagAdded) onFirstTagAdded(tag.name);
                  })
                  .catch(console.error);
              }
              // Detect todo/inProgress → consumed transition to fire the
              // logbook-hint callback (same moment as first-tag-added).
              const CONSUMED_TRIGGER = new Set(["todo", "inProgress"]);
              const prevGmt = (myEvent.eventTags || []).find(t => [
                "like","veryGood","good","finished","bad","noRating","inProgress","todo"
              ].includes(t.name));
              const consumedNow = prevGmt && CONSUMED_TRIGGER.has(prevGmt.name) && !CONSUMED_TRIGGER.has(tag.name);
              return axiosInstance
                .post(`/artEvents/tag/${myEvent.id}/globalManualTag`, { tagId: tag.id })
                .then((response) => {
                  // Response is the new tag; refetch event or patch state.
                  // Cheapest: patch eventTags in-place — swap old global-manual for new.
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: [
                      ...(prev.eventTags || []).filter(t => ![
                        "like","veryGood","good","finished","bad","noRating","inProgress","todo"
                      ].includes(t.name)),
                      response.data,
                    ],
                  }) : prev);
                  if (consumedNow && onFirstTagAdded) onFirstTagAdded(tag.name);
                })
                .catch(console.error);
            }}
            onAddCustomTag={(name) => {
              if (!myEvent) return;
              return axiosInstance
                .post(`/artEvents/tag/${myEvent.id}/customTag`, { name })
                .then((response) => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: [...(prev.eventTags || []), response.data],
                  }) : prev);
                })
                .catch(console.error);
            }}
            onRemoveCustomTag={(tagId) => {
              if (!myEvent) return;
              return axiosInstance
                .delete(`/artEvents/tag/${myEvent.id}/customTag`, { data: { tagId } })
                .then(() => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: (prev.eventTags || []).filter(t => t.id !== tagId),
                  }) : prev);
                })
                .catch(console.error);
            }}
          />
          {/* The chat_icon / "Go to discussion" button used to live here.
              Replaced by the library link rendered above the tags row.
              (Keeping the surrounding row so tags stay in the same
              horizontal container as before for spacing.) */}
        </div>
      </div>
      </div>
      {isArtEventDropdownOpen && (
        <ArtEventDropdown
          eventId={myEvent.id} // Pass myEvent.id explicitly
          onClose={() => setIsArtEventDropdownOpen(false)} // Close dropdown handler
          language={language}
          eventType="artEvent"
          initialArtEvent={artEvent}
          art={art}
          // Keep the first-row tag pill in sync with edits made inside
          // the overlay — without this, the overlay updates only its
          // local state and the outer row (and next overlay open, which
          // re-seeds from initialArtEvent) shows the stale tag.
          onEventUpdated={(updatedEvent) => {
            setMyEvent(updatedEvent);
            setArtEvent((prev) => (prev ? { ...prev, event: updatedEvent } : prev));
          }}
        />
      )}
    </div>
  );
};

export default CardEventArtChatMini;